import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone"

export default class extends Controller {
  static targets = ["input", "label", "dropzone"]

  connect() {
    console.log("File upload controller connected")
    
    this.dropzone = new Dropzone(this.dropzoneTarget, {
      url: this.element.action || "/onboarding",
      autoProcessQueue: false,
      maxFiles: 1,
      maxFilesize: 5, // 5MB
      acceptedFiles: "image/png,image/jpeg,image/jpg",
      uploadMultiple: false,
      dictDefaultMessage: "Drop files here or click to upload",
      dictFileTooBig: "File is too big ({{filesize}}MB). Max filesize: {{maxFilesize}}MB.",
      dictInvalidFileType: "You can't upload files of this type.",
      previewTemplate: `
        <div class="dz-preview dz-file-preview w-full">
          <div class="relative">
            <!-- Preview container -->
            <div class="overflow-hidden rounded-lg bg-gray-100 border border-gray-200">
              <!-- Image preview -->
              <div class="dz-image flex justify-center items-center p-4" style="min-height: 200px;">
                <img data-dz-thumbnail class="rounded max-h-48 max-w-full object-contain" />
                <!-- PDF preview -->
                <div class="pdf-preview hidden flex-col items-center justify-center space-y-2">
                  <svg class="h-16 w-16 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <span class="text-sm text-gray-600" data-dz-name></span>
                </div>
              </div>
              
              <!-- File info -->
              <div class="p-4 border-t border-gray-200 bg-white">
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-2">
                    <span class="text-sm font-medium text-gray-900" data-dz-name></span>
                    <span class="text-sm text-gray-500" data-dz-size></span>
                  </div>
                  <button type="button" class="dz-remove text-sm text-red-600 hover:text-red-800" data-dz-remove>
                    Remove file
                  </button>
                </div>
                
                <!-- Error message -->
                <div class="dz-error-message mt-2">
                  <span class="text-sm text-red-600" data-dz-errormessage></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      `,
      init: function() {
        // Display existing file if present
        const input = this.element.querySelector('input[type="file"]')
        if (input && input.dataset.existingFile) {
          const mockFile = {
            name: input.dataset.existingFile,
            size: parseInt(input.dataset.fileSize) || 0,
            type: input.dataset.fileType || 'image/png,image/jpeg,image/jpg',
            status: Dropzone.ADDED,
            accepted: true
          }

          // Remove required attribute since we have an existing file
          input.removeAttribute('required')

          // Emit addedfile event with the mock file
          this.emit("addedfile", mockFile)
          // For images, create a thumbnail using the encrypted file URL
          const fileType = this.element.dataset.fileUploadDropzoneIdValue
          const previewUrl = `/encrypted_files/${fileType}`
          this.emit("thumbnail", mockFile, previewUrl)

          // Hide the initial upload UI
          const uploadUI = this.element.querySelector('.space-y-1')
          if (uploadUI) {
            uploadUI.style.display = 'none'
          }

          // Add the file to the list of files
          this.files.push(mockFile)
        }

        this.on("addedfile", (file) => {
          // Update the label text
          const labelText = this.element.querySelector('.file-label')
          if (labelText) {
            labelText.textContent = file.name
          }

          // Update the hidden input
          const input = this.element.querySelector('input[type="file"]')
          if (input && file instanceof File) {  // Only update if it's a new file, not our mock file
            const dataTransfer = new DataTransfer()
            dataTransfer.items.add(file)
            input.files = dataTransfer.files

            // Add required attribute back when uploading a new file
            input.setAttribute('required', '')
          }

          // Hide the initial upload UI
          const uploadUI = this.element.querySelector('.space-y-1')
          if (uploadUI) {
            uploadUI.style.display = 'none'
          }
        })

        this.on("removedfile", () => {
          // Reset the label text
          const labelText = this.element.querySelector('.file-label')
          if (labelText) {
            labelText.textContent = "Upload a file"
          }

          // Clear the hidden input and make it required again
          const input = this.element.querySelector('input[type="file"]')
          if (input) {
            input.value = ""
            input.setAttribute('required', '')
          }

          // Show the initial upload UI
          const uploadUI = this.element.querySelector('.space-y-1')
          if (uploadUI) {
            uploadUI.style.display = 'block'
          }
        })
      }
    })

    // Handle file input change
    const input = this.element.querySelector('input[type="file"]')
    if (input) {
      input.addEventListener('change', (e) => {
        const file = e.target.files[0]
        if (file) {
          // Remove any existing files from dropzone
          if (this.dropzone.files.length > 0) {
            this.dropzone.removeAllFiles(true)
          }
          // Add the file to dropzone
          this.dropzone.addFile(file)
        }
      })
    }
  }

  disconnect() {
    if (this.dropzone) {
      this.dropzone.destroy()
    }
  }
} 